import React, { useState, useRef, useEffect } from 'react';
import { audioManager } from '../services/AudioService';
import TermsBadge from './TermsBadge';

const TransformationCard = ({
    isOpen,
    onClose,
    data: rawData, // Rename prop to rawData to avoid conflict
    sourceItemName,
    onComplete,
    babyName = 'Bebeğiniz',
    hasBaby = false,
    children = []
}) => {
    // Veri içindeki {{babyName}} alanlarını dinamikleştir
    const data = React.useMemo(() => {
        if (!rawData) return null;

        const replaceText = (text) => {
            if (typeof text !== 'string') return text;
            return text.replace(/{{babyName}}/g, babyName);
        };

        return {
            ...rawData,
            hasBaby, // Pass this through to data for rendering check
            babySafetyStep: replaceText(rawData.babySafetyStep),
            description: replaceText(rawData.description),
            steps: (rawData.steps || []).map(step => replaceText(step)),
            storageInstructions: replaceText(rawData.storageInstructions)
        };
    }, [rawData, babyName, hasBaby]);

    const [activeTimers, setActiveTimers] = useState({});
    const [isReadingAll, setIsReadingAll] = useState(false);
    const abortControllerRef = useRef(null);

    if (!isOpen || !data) return null;

    // --- AKILLI ASİSTAN (AYNI MANTIK) ---
    const handleSmartFlowSpeak = async () => {
        if (isReadingAll) {
            stopSpeaking();
            return;
        }
        setIsReadingAll(true);
        window.speechSynthesis.cancel();
        audioManager.stopAmbience();

        abortControllerRef.current = new AbortController();
        const steps = data.steps || [];

        // Intro
        const introMsg = new SpeechSynthesisUtterance(`${sourceItemName} kurtarma operasyonu başlıyor. Hedefimiz: ${data.targetName}. Hazırsanız başlayalım.`);
        introMsg.lang = 'tr-TR';
        await new Promise(r => { introMsg.onend = r; window.speechSynthesis.speak(introMsg); });

        for (let i = 0; i < steps.length; i++) {
            if (!isReadingAll && !isOpen) break;
            const stepText = steps[i];

            const utterance = new SpeechSynthesisUtterance(`${i + 1}. adım. ${stepText}`);
            utterance.lang = 'tr-TR';

            await new Promise(resolve => {
                utterance.onend = resolve;
                window.speechSynthesis.speak(utterance);
            });

            // Adımlar arası kısa bekleme
            if (i < steps.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
        }

        if (isOpen && isReadingAll) {
            const finalMsg = new SpeechSynthesisUtterance(`Harika iş çıkardınız şefim. ${data.targetName} stoklara eklendi.`);
            finalMsg.lang = 'tr-TR';
            window.speechSynthesis.speak(finalMsg);
        }
        setIsReadingAll(false);
    };

    const stopSpeaking = () => {
        window.speechSynthesis.cancel();
        audioManager.stopAmbience();
        setIsReadingAll(false);
        if (abortControllerRef.current) abortControllerRef.current.abort();
    };

    const handleStepSpeak = (idx, text) => {
        stopSpeaking();
        const utterance = new SpeechSynthesisUtterance(`${idx + 1}. adım. ${text}`);
        utterance.lang = 'tr-TR';
        window.speechSynthesis.speak(utterance);
    };

    const handleModalClose = () => {
        stopSpeaking();
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={(e) => e.target === e.currentTarget && handleModalClose()}>
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md transition-opacity"></div>

            <div className="relative bg-white dark:bg-[#1a1a1a] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in-up border border-slate-200 dark:border-gray-700">

                {/* Header - NO IMAGE, Just Typo */}
                <div className="p-6 border-b border-slate-100 dark:border-gray-800 bg-slate-50 dark:bg-gray-900/50 flex justify-between items-start">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-[10px] font-black px-2 py-1 rounded-lg uppercase tracking-wider flex items-center gap-1">
                                <span className="material-symbols-outlined text-[14px]">recycling</span>
                                Gıda Kurtarma Timi
                            </span>
                            <span className="text-slate-400 dark:text-gray-500 text-xs font-bold uppercase tracking-wider">
                                {new Date().toLocaleDateString('tr-TR')}
                            </span>
                        </div>
                        <h2 className="text-1xl font-black text-slate-800 dark:text-white uppercase tracking-tight flex items-center gap-2">
                            <span className="text-red-500 line-through decoration-2 opacity-60">{sourceItemName}</span>
                            <span className="material-symbols-outlined text-slate-300">arrow_forward</span>
                            <span className="text-primary">{data.targetName}</span>
                        </h2>
                        <p className="text-slate-500 dark:text-gray-400 text-sm font-medium mt-1">
                            {data.description}
                        </p>
                    </div>
                    <button
                        onClick={handleModalClose}
                        className="w-10 h-10 flex items-center justify-center bg-white dark:bg-gray-800 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all shadow-sm ring-1 ring-black/5"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                <div className="p-6 space-y-8">
                    {/* Info Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-blue-50 dark:bg-blue-900/10 p-3 rounded-xl border border-blue-100 dark:border-blue-900/20 text-center">
                            <span className="material-symbols-outlined text-blue-500 text-xl mb-1">hourglass_top</span>
                            <p className="text-[10px] text-blue-400 uppercase font-bold">Yeni Ömür</p>
                            <p className="text-sm font-black text-blue-700 dark:text-blue-300">+{data.newExpiryDays} Gün</p>
                        </div>
                        <div className="bg-orange-50 dark:bg-orange-900/10 p-3 rounded-xl border border-orange-100 dark:border-orange-900/20 text-center">
                            <span className="material-symbols-outlined text-orange-500 text-xl mb-1">inventory_2</span>
                            <p className="text-[10px] text-orange-400 uppercase font-bold">Saklama</p>
                            <p className="text-sm font-black text-orange-700 dark:text-orange-300">{data.storageLocation}</p>
                        </div>
                        <div className="bg-purple-50 dark:bg-purple-900/10 p-3 rounded-xl border border-purple-100 dark:border-purple-900/20 text-center col-span-2 relative group overflow-hidden">
                            <span className="material-symbols-outlined text-purple-500 text-xl mb-1">child_care</span>
                            <p className="text-[10px] text-purple-400 uppercase font-bold">Bebek Güvenliği</p>

                            <div className="flex items-center justify-center gap-2 mt-1">
                                <p className="text-xs font-bold text-purple-700 dark:text-purple-300 leading-tight">
                                    {data.babySafetyStep ? "" : "Standart"}
                                </p>
                                {data.babySafetyStep && (
                                    <div className="relative w-6 h-6">
                                        <TermsBadge className="!absolute !top-0 !right-0 !static transform hover:scale-110" />
                                        {/* Note: TermsBadge is absolute by default, overriding with !static for this layout or wrapping container */}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Lunchbox Recommendation */}
                    {data.isLunchbox && children.length > 0 && (
                        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 border-l-4 border-emerald-500 rounded-r-xl">
                            <h4 className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-bold uppercase text-xs mb-1">
                                <span className="material-symbols-outlined text-base">lunch_dining</span>
                                Beslenme Çantası Fikri 🍱
                            </h4>
                            <p className="text-sm text-emerald-800 dark:text-emerald-100 font-medium leading-snug">
                                Bu tarif hafta içi beslenme çantası için harika!
                                <strong> {children[0].name}</strong> için bir porsiyon kenara ayırmayı unutma.
                            </p>
                        </div>
                    )}

                    {/* Baby Safety Warning (Only if User has Baby) */}
                    {data.hasBaby && data.babySafetyStep && (
                        <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
                            <h4 className="flex items-center gap-2 text-red-700 dark:text-red-400 font-bold uppercase text-xs mb-1">
                                <span className="material-symbols-outlined text-base">child_care</span>
                                Bebek Koruması
                            </h4>
                            <p className="text-sm text-red-800 dark:text-red-200 font-medium leading-snug">
                                {data.babySafetyStep}
                            </p>
                        </div>
                    )}

                    {/* Steps */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-black text-lg text-slate-800 dark:text-white uppercase flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary">format_list_numbered</span>
                                Dönüşüm Adımları
                            </h3>
                            <button
                                onClick={handleSmartFlowSpeak}
                                className={`${isReadingAll ? 'bg-red-500' : 'bg-primary'} text-white px-3 py-1.5 rounded-lg text-[10px] font-black flex items-center gap-2 transition-colors shadow-lg shadow-primary/20`}
                            >
                                <span className="material-symbols-outlined text-[16px]">{isReadingAll ? 'stop_circle' : 'play_circle'}</span>
                                {isReadingAll ? 'DURDUR' : 'SESLİ BAŞLAT'}
                            </button>
                        </div>

                        <div className="space-y-3">
                            {data.steps.map((step, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => handleStepSpeak(idx, step)}
                                    className="flex gap-4 p-4 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5 hover:border-primary/30 cursor-pointer transition-all group"
                                >
                                    <span className="shrink-0 size-8 bg-white dark:bg-white/10 rounded-full flex items-center justify-center font-black text-slate-400 group-hover:bg-primary group-hover:text-white transition-colors shadow-sm text-sm">
                                        {idx + 1}
                                    </span>
                                    <p className="text-slate-700 dark:text-gray-300 font-medium leading-relaxed group-hover:text-slate-900 dark:group-hover:text-white">
                                        {step}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Storage Instructions */}
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-100 dark:border-amber-900/20 flex gap-3">
                        <span className="material-symbols-outlined text-amber-500 shrink-0">inventory</span>
                        <div>
                            <h5 className="font-bold text-amber-700 dark:text-amber-400 text-xs uppercase mb-1">Paketleme & Saklama</h5>
                            <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">{data.storageInstructions}</p>
                        </div>
                    </div>

                </div>

                {/* Footer Actions */}
                <div className="sticky bottom-0 p-6 bg-white/95 dark:bg-[#1a1a1a]/95 backdrop-blur-md border-t border-slate-100 dark:border-gray-800 flex gap-4 z-30">
                    <button
                        onClick={handleModalClose}
                        className="flex-1 h-14 bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-300 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors uppercase text-xs tracking-wider"
                    >
                        Vazgeç
                    </button>
                    <button
                        onClick={onComplete}
                        className="flex-[2] h-14 bg-green-500 hover:bg-green-600 text-white rounded-2xl font-black text-sm uppercase tracking-wide shadow-xl shadow-green-500/20 flex items-center justify-center gap-3 transition-all transform active:scale-95"
                    >
                        <div>
                            <span className="block text-[10px] opacity-80 font-medium normal-case">Operasyon Tamamlandı</span>
                            <span className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-xl">check_circle</span>
                                STOKLARI GÜNCELLE
                            </span>
                        </div>
                    </button>
                </div>

            </div>
        </div>
    );
};

export default TransformationCard;
